import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { countNotSent, countQuarantined, countUnsent, forgetPullCursors, syncAll } from '../data/sync';
import type { SyncOutcome } from '../data/sync';
import { report, отпустить } from '../sentry';
import { useProfiles } from './ProfilesContext';
import { useTrainerContext } from './TrainerContext';
import { looksOffline } from '../data/offline';

/** Четыре причины не начинать обмен, вынесенные отдельно, чтобы их можно
 *  было проверить прогоном, а не глазами.
 *
 *  - **Учётной записи нет вовсе** (`parentId === null`). Взрослый занимается
 *    без входа, и обмену некуда идти. Это не отказ и не происшествие:
 *    экран говорит об этом отдельной строкой, а Sentry об этом молчит.
 *  - **Обмен уже идёт.** Второй заход дублировал бы отправку и гонял бы
 *    курсоры навстречу друг другу. Сам запрос при этом НЕ теряется:
 *    `sync()` запоминает его, и по концу прохода идёт один дополнительный.
 *  - **Идёт тренировка.** Сеть не должна конкурировать с экраном, на
 *    котором меряется время ответа ребёнка.
 *  - **Токен мёртв** (сессия протухла, а обновить её не вышло без сети). С
 *    таким токеном сервер отвечает отказом на всё; пытаться значит
 *    помечать профили сломанными на ровном месте и звать родителя чинить
 *    то, что чинится одним входом. */
export function shouldSync(
  { parentId, running, training, stale }:
    { parentId: string | null; running: boolean; training: boolean; stale: boolean },
): boolean {
  return parentId !== null && !running && !training && !stale;
}

export type SyncState =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'done'; at: number; outcome: SyncOutcome }
  | { kind: 'failed'; at: number; offline: boolean };

export interface SyncValue {
  state: SyncState;
  /** Сколько тренировок ещё не уехало. Показывается родителю и называется
   *  числом в диалоге выхода: стирание необратимо. */
  unsent: number;
  /** Строки, которые сервер отдал, а журнал не принял. Показываются
   *  родителю: молчать о том, что часть занятий не доехала, нельзя. */
  quarantined: number;
  /** Занятия, которые сервер не примет никогда. Считается из базы: след
   *  обязан пережить не только этот обмен, но и все следующие. */
  notSent: number;
  sync: () => Promise<void>;
  recount: () => Promise<void>;
  /** Забыть, что докачано, и перечитать журналы с нуля. Восстановление на
   *  случай, которого курсор не покрывает: местная строка пропала уже
   *  после того, как курсор за неё ушёл. */
  reread: () => Promise<void>;
}

const SyncContext = createContext<SyncValue | null>(null);

export function useSync(): SyncValue {
  const value = useContext(SyncContext);
  if (!value) throw new Error('синхронизация вне SyncProvider');
  return value;
}

const offlineError = looksOffline;

/** Заводила обмена.
 *
 *  Когда: при открытии приложения, после завершения тренировки и при
 *  возврате связи. НЕ во время тренировки — сеть не должна конкурировать с
 *  отзывчивостью экрана, на котором меряется время ответа ребёнка.
 *
 *  Почему отказ обязан быть виден. Бесплатный тариф Supabase останавливает
 *  проект после недели без запросов, и тогда синхронизация перестаёт
 *  работать. Без показа на экране это выглядит как «всё хорошо»: занятия
 *  идут, журнал пишется, а на второе устройство ничего не приезжает — и
 *  узнать об этом неоткуда. */
export function SyncProvider({
  parentId, stale, children,
}: { parentId: string | null; stale: boolean; children: ReactNode }) {
  const [state, setState] = useState<SyncState>({ kind: 'idle' });
  const [unsent, setUnsent] = useState(0);
  const [quarantined, setQuarantined] = useState(0);
  const [notSent, setNotSent] = useState(0);
  const { refresh } = useProfiles();
  const { training } = useTrainerContext();
  const running = useRef(false);
  const alive = useRef(true);
  /** Тренировка идёт — читается из ссылки, чтобы обработчик события
   *  `online` не пересоздавался на каждый ответ ребёнка. */
  const trainingRef = useRef(training);
  trainingRef.current = training;

  const recount = useCallback(async () => {
    const [count, held, skipped] = await Promise.all([
      countUnsent(), countQuarantined(), countNotSent(),
    ]);
    if (alive.current) {
      setUnsent(count);
      setQuarantined(held);
      setNotSent(skipped);
    }
  }, []);

  /** Мёртвый токен — читается из ссылки, чтобы не пересоздавать обмен на
   *  каждое изменение состояния. */
  const staleRef = useRef(stale);
  staleRef.current = stale;

  /** Запрос, пришедший ПОСРЕДИ прохода. Раньше он терялся: `sync()`
   *  видел `running`, пересчитывал числа и выходил, и надгробие удалённого
   *  ребёнка, занятие, кончившееся во время обмена, и «перечитать заново»
   *  (курсоры уже забыты) ждали следующего повода — а его могло не быть до
   *  перезагрузки. Теперь запрос запоминается ОДНИМ обещанием, и по концу
   *  прохода идёт ровно один дополнительный, сколько бы запросов ни
   *  пришло. Обещание, которое получил вызывающий, разрешается по концу
   *  дополнительного прохода: `await reread()` значит «перечитано». */
  const queued = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);

  /** Один проход: состояние, `syncAll`, разбор исхода. Наружу не бросает
   *  (отказ уходит в `state` и, если не сетевой, в Sentry). */
  const runPass = useCallback(async () => {
    setState({ kind: 'running' });
    try {
      /** Проверка избыточна для типа, но не для чтения: `shouldSync` в
       *  `sync` уже отсеял отсутствие записи, и здесь мы это проговариваем
       *  вслух, а не глушим восклицательным знаком. */
      if (parentId === null) return;
      const outcome = await syncAll(parentId);
      /** Список профилей мог измениться: сервер снёс удалённого на другом
       *  устройстве или прислал заведённого там же. */
      if (outcome.removed.length > 0 || outcome.pulled > 0) await refresh();
      if (alive.current) setState({ kind: 'done', at: Date.now(), outcome });
    } catch (error) {
      const offline = offlineError(error);
      /** Отсутствие сети — не происшествие, и слать его в Sentry значит
       *  утопить настоящие отказы в шуме метро и лифтов. */
      if (!offline) report(error, 'syncAll');
      if (alive.current) setState({ kind: 'failed', at: Date.now(), offline });
    }
  }, [parentId, refresh]);

  const sync = useCallback(async () => {
    /** `running` здесь НЕ учитывается: «идёт проход» — не отказ, а повод
     *  запомнить запрос (ниже). Отказ — остальные три причины. */
    const allowed = shouldSync({
      parentId, running: false, training: trainingRef.current, stale: staleRef.current,
    });
    if (!allowed) {
      /** Пересчёт нужен и тогда, когда обмен не пошёл: без учётной записи
       *  число «не отправлено» — единственное, чем экран может сказать
       *  правду о том, сколько занятий держится на одном устройстве. */
      await recount();
      return;
    }
    if (running.current) {
      /** Проход идёт, запрос законный: запоминаем, не теряем. Второй
       *  `syncAll` поверх идущего не зовётся — он гонял бы курсоры
       *  навстречу друг другу. */
      if (!queued.current) {
        let resolve!: () => void;
        const promise = new Promise<void>((done) => { resolve = done; });
        queued.current = { promise, resolve };
      }
      await queued.current.promise;
      return;
    }
    running.current = true;
    try {
      await runPass();
      /** Ровно один дополнительный проход на всё, что накопилось; запросы,
       *  пришедшие во время него самого, дают следующий. После
       *  размонтирования не запускается. */
      while (queued.current && alive.current) {
        const waiting = queued.current;
        queued.current = null;
        /** Условия входа перепроверяются: пока шёл проход, могла начаться
         *  тренировка или умереть токен. Потери нет — конец тренировки сам
         *  зовёт `sync()`, а с мёртвым токеном обмен невозможен. */
        if (!shouldSync({
          parentId, running: false, training: trainingRef.current, stale: staleRef.current,
        })) {
          waiting.resolve();
          break;
        }
        try {
          await runPass();
        } finally {
          waiting.resolve();
        }
      }
    } finally {
      running.current = false;
      /** Размонтирование с запросом в очереди: отпускаем ждущего. */
      queued.current?.resolve();
      queued.current = null;
      await recount();
    }
  }, [parentId, recount, runPass]);

  /** Обмен сразу после тренировки. Ловится переход «шла → кончилась», а не
   *  само появление сводки: тренировка может и оборваться выходом, и
   *  ответы, которые ребёнок успел дать, тоже надо отправить. Если в этот
   *  миг идёт проход (тренировка кончилась посреди обмена), запрос не
   *  теряется: `sync()` запоминает его и делает один дополнительный проход.
   *
   *  `sync()` сам не отказывает никогда НАПРЯМУЮ — путь `syncAll` целиком в
   *  try/catch, и экран уже получил своё «done»/«failed» до него, — но
   *  `recount()` в двух местах (строка выше и `finally`) не обёрнут и может
   *  отклониться от настоящего чтения IndexedDB (тот же обрыв соединения,
   *  что и в `src/data/dbReconnect.ts`). Кнопки обмена не привязаны к
   *  занятости, зависящей от `sync()`, — экран не виснет, — а числа
   *  «не отправлено» просто останутся прежними до следующего обмена.
   *  `отпустить(..., 'sync')` называет отказ вместо того, чтобы отдавать его
   *  безымянному глобальному перехватчику. */
  const wasTraining = useRef(training);
  useEffect(() => {
    if (wasTraining.current && !training) отпустить(sync(), 'sync');
    wasTraining.current = training;
  }, [training, sync]);

  useEffect(() => {
    alive.current = true;
    отпустить(sync(), 'sync');
    const onOnline = () => отпустить(sync(), 'sync');
    window.addEventListener('online', onOnline);
    return () => {
      alive.current = false;
      window.removeEventListener('online', onOnline);
    };
  }, [sync]);

  const reread = useCallback(async () => {
    await forgetPullCursors();
    await sync();
  }, [sync]);

  return (
    <SyncContext.Provider value={{ state, unsent, quarantined, notSent, sync, recount, reread }}>
      {children}
    </SyncContext.Provider>
  );
}

/** Одна строка о состоянии обмена. Пусто, когда всё в порядке: постоянная
 *  зелёная галочка учит не смотреть на это место. */
export function syncLine(
  state: SyncState, unsent: number, quarantined = 0, stale = false, signedIn = true,
  notSent = 0,
): string | null {
  if (!signedIn) {
    /** Без учётной записи строка НЕ пуста, и это намеренно. Пустая строка
     *  здесь означала бы «всё в порядке», а в порядке ровно то, что занятия
     *  держатся на одном устройстве и исчезнут вместе с ним. */
    return unsent > 0
      ? `Занятия только на этом устройстве: ${unsent}. Войдите, чтобы они сохранились.`
      : 'Занятия хранятся только на этом устройстве. Войдите, чтобы они сохранились.';
  }
  if (stale) {
    /** Занятия идут и сохраняются на устройстве — про это надо сказать
     *  первым, иначе строка читается как «всё пропало». */
    return 'Занятия сохраняются на устройстве. Чтобы они уезжали на другие, войдите снова.';
  }
  if (state.kind === 'running') return 'Синхронизация…';
  if (state.kind === 'failed') {
    return state.offline
      ? 'Нет связи. Занятия сохранены на устройстве и уедут, когда связь вернётся.'
      : 'Сервер не отвечает. Занятия сохранены на устройстве.';
  }
  if (state.kind === 'done' && state.outcome.needsUpdate) {
    return 'Другое устройство работает на более новой версии. Обновите приложение, чтобы получить его занятия.';
  }
  if (quarantined > 0) {
    /** Строки были на сервере и в журнал не попали. Молчать об этом нельзя:
     *  занятия идут, журнал выглядит полным, и узнать неоткуда. */
    return `Часть записей не удалось прочитать: ${quarantined}. Мы уже знаем об этой ошибке.`;
  }
  if (notSent > 0) {
    /** Строки, которые сервер не примет никогда.
     *
     *  Число берётся из БАЗЫ, а не из ответа обмена, и в этом вся суть
     *  правки. Пока оно жило в ответе, строка на экране показывалась один
     *  раз — на том обмене, где случился пропуск, — и на следующем
     *  исчезала. Родитель, посмотревший не в тот момент, не узнавал
     *  никогда, а занятия так и не появлялись на втором устройстве. */
    return `Часть занятий сервер не принял: ${notSent}. Они остались на этом устройстве, но не попадут на второе. Мы уже знаем об этой ошибке.`;
  }
  if (state.kind === 'done' && state.outcome.stuck.length > 0) {
    /** Профиль встал, остальные идут. Молчать нельзя: у второго ребёнка всё
     *  хорошо, а занятия первого не уезжают. */
    return `У одного из детей занятия не отправляются: ${state.outcome.stuck.length}. Мы уже знаем об этой ошибке.`;
  }
  if (state.kind === 'done' && state.outcome.lookConflicts.length > 0) {
    /** Запасной сигнал. Главная проверка — у сервера в момент заведения и
     *  смены облика (`lookTakenOnServer`); сюда доходит облик, занятый
     *  тогда, когда связи не было. */
    return 'Такой цвет и зверёк уже заняты другим ребёнком. Смените облик, иначе занятия не будут синхронизированы.';
  }
  if (unsent > 0) return `Не отправлено тренировок: ${unsent}.`;
  return null;
}
